import Box from "@memorang/ui/components/Box";

import { useAuth } from "@clerk/clerk-expo";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { BottomSheetModalProvider } from "@gorhom/bottom-sheet";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import type { WelcomeDialogSchemaType } from "@memorang/constants/config/schema";
import type { BaseItemSchema } from "@memorang/types";
import { SessionAutoStartContext } from "@memorang/ui/components/StudySession/SessionAutoStartContext";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { useCallback, useRef, useState } from "react";
import { ScrollView } from "react-native";
import { getSessionStartDialogConfig } from "../constants/dialog-configs";
import { PassageStemModalContainer } from "../containers/PassageStemModalContainer";
import StudySessionFeedbackContainer from "../containers/StudySessionFeedbackContainer";
import StudySessionShellContainer from "../containers/StudySessionShellContainer";
import { SessionItemsNavLayoutProvider } from "../contexts/SessionItemsNavLayoutContext";
import { hasListeningItems } from "../helpers/content";
import useSessionStore from "../hooks/useSessionStore";
import { useStartDialogStore } from "../hooks/useStartDialogStore";
import DialogSession from "./SessionDialog";
import SessionListDesktop from "./SessionList/SessionListDesktop";

type Props = {
	sessionItems: BaseItemSchema[];
	sessionId: number;
	currentBlockIndex: number;
	totalTime: number;
	inReviewMode?: boolean;
	reportSessionType?: "PRACTICE_TEST" | "DIAGNOSTIC";
	numSections: number;
	showDisclaimer: boolean;
	numTotalItems: number;
};

const RenderStartDialog = ({
	examName,
	totalTime,
	reportSessionType,
	showStartDialog,
	handleCloseStartDialog,
	numSections,
	welcomeDialog,
	numTotalItems,
	hasListening,
}: {
	examName?: string;
	totalTime: number;
	reportSessionType?: "PRACTICE_TEST" | "DIAGNOSTIC";
	showStartDialog: boolean;
	handleCloseStartDialog: () => void;
	numSections: number;
	welcomeDialog?: WelcomeDialogSchemaType;
	numTotalItems: number;
	hasListening: boolean;
}) => {
	const diagnosticLabel = useAppStore((state) => state.exam?.diagnosticLabel);

	if (!showStartDialog) {
		return null;
	}

	const customConfig = getSessionStartDialogConfig({
		examName: examName || "",
		totalTime,
		isQuiz: reportSessionType === "DIAGNOSTIC",
		numSections,
		numTotalQuestions: numTotalItems,
		diagnosticLabel,
		welcomeDialog,
		hasListening,
	});

	return (
		<DialogSession
			customConfig={customConfig}
			open
			handleClose={handleCloseStartDialog}
			handleClickButton={handleCloseStartDialog}
			paragraphMarginVertical={welcomeDialog ? 8 : undefined}
		/>
	);
};

const PracticeSessionView = ({
	sessionItems,
	totalTime,
	inReviewMode,
	reportSessionType,
	numSections,
	showDisclaimer,
	numTotalItems,
}: Props) => {
	const {
		currentExamContext: { examId },
	} = useExamContext();

	const welcomeDialog = useAppStore(
		(state) => state.sessionView?.welcomeDialog,
	);
	const [showReadMore, setShowReadMore] = useState(false);

	const toggleShowReadMore = useCallback(() => {
		setShowReadMore((prev) => !prev);
	}, []);

	const shownStartDialogs = useStartDialogStore(
		(store) => store.shownStartDialogs,
	);
	const setStartDialogShown = useStartDialogStore(
		(store) => store.setStartDialogShown,
	);

	const sessionId = useSessionStore((state) => state.id);
	const { userId } = useAuth();
	const passageStemModalRef = useRef<{ scrollToHighlightedText: () => void }>(
		null,
	);
	const examName = useSessionStore((state) => state.examName);
	const isResumedSession = useSessionStore((state) => state.isResumedSession);
	const isFinalizingSession = useSessionStore((state) =>
		Boolean(state.isFinalizingSession),
	);
	const theme = useAppTheme();
	const { isDesktopWeb } = useDevice();
	const testDriver = useAppStore((state) => state.sessionView?.testDriver);
	const isPteDriver = testDriver === "pte";

	const showAudioTestDialogConfig = useAppStore(
		(state) => state.app?.accountSettings?.showAudioTestDialog,
	);
	const showAudioPlaybackTestDialogGlobal = useGlobalStore(
		(state) => state.showAudioPlaybackTestDialog,
	);
	const [showAudioPlaybackTestDialog, setShowAudioPlaybackTestDialog] =
		useState(
			Boolean(
				showAudioPlaybackTestDialogGlobal &&
					showAudioTestDialogConfig &&
					hasListeningItems([sessionItems]),
			),
		);

	const startDialogShownKey = `user:${userId}:session:${sessionId}:${examId}`;

	const showStartDialog = !(
		shownStartDialogs?.[startDialogShownKey] ||
		inReviewMode ||
		showDisclaimer ||
		isPteDriver ||
		isResumedSession
	);
	// const showStartDialog = true;

	const handleCloseStartDialog = () => {
		setStartDialogShown(startDialogShownKey);
	};

	return (
		<SessionItemsNavLayoutProvider>
			<SessionAutoStartContext.Provider
				value={
					!(
						showStartDialog ||
						showDisclaimer ||
						showAudioPlaybackTestDialog ||
						isFinalizingSession
					)
				}
			>
				<Box flex={1} backgroundColor={theme.colors.surface} minWidth={0}>
					<BottomSheetModalProvider>
						<StudySessionShellContainer
							showStartDialog={showStartDialog || showDisclaimer}
							showAudioPlaybackTestDialog={showAudioPlaybackTestDialog}
							onCloseAudioPlaybackTestDialog={() =>
								setShowAudioPlaybackTestDialog(false)
							}
						>
							<ScrollView
								contentContainerStyle={{ flexGrow: 1, height: "100%" }}
							>
								<Box
									flex={1}
									height="100%"
									backgroundColor={theme.colors.surface}
									accessibilityLabel="Session View, containing the question, and available options."
									// biome-ignore lint/a11y/useSemanticElements: intentional role override
									role="main"
								>
									<SessionListDesktop
										items={sessionItems}
										toggleShowReadMore={toggleShowReadMore}
									/>
									{!isDesktopWeb && <StudySessionFeedbackContainer />}
								</Box>
							</ScrollView>
						</StudySessionShellContainer>
					</BottomSheetModalProvider>

					<RenderStartDialog
						examName={examName}
						totalTime={totalTime}
						reportSessionType={reportSessionType}
						showStartDialog={showStartDialog}
						handleCloseStartDialog={handleCloseStartDialog}
						numSections={numSections}
						welcomeDialog={welcomeDialog}
						numTotalItems={numTotalItems}
						hasListening={hasListeningItems([sessionItems])}
					/>
					<PassageStemModalContainer
						passageStemModalRef={passageStemModalRef}
						showReadMore={showReadMore}
						showStartDialog={showStartDialog || showDisclaimer}
						propToggleShowReadMore={() => setShowReadMore(false)}
					/>
				</Box>
			</SessionAutoStartContext.Provider>
		</SessionItemsNavLayoutProvider>
	);
};

export default PracticeSessionView;
